import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { useDocument } from '@nandorojo/swr-firestore';
import { IgalleryItem } from '@typings/pages/gallery.interface';
import ArrowBtn from '@components/dataEntry/button/ArrowBtn/ArrowBtn';
import { ACCESS_TYPE_MAP, ACCESS_TYPE } from '@typings/common/accessType';
import { getPathKey } from '@utils/getPath';
import { deleteGallery } from '@store/firebase/gallery/delete-gallery';
import ControlIcon from '@template/ControlSec/ControlIcon';
import GuardPage from '@template/PermissionItem/GuardPage';
import { useReadPermission } from '@hooks/usePermission';
import { nextPage, prevPage } from '@store/firebase/infinitePaginate';
import FitImage from '@template/Image/FitImage';
import UpdateGalleryModal from '@pages/Gallery/UpdateGallery/UpdateGalleryModal';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import { scrollbarStyle } from '@styles/variants/scrollbar';

const SlideLeftBtn = styled.div`
  position: absolute;
  z-index: 10;
  width: 54px;
  height: 54px;

  top: 50%;
  transform: translateY(-50%);
  left: 12px;
`;

const SlideRightBtn = styled.div`
  position: absolute;
  z-index: 10;
  width: 54px;
  height: 54px;

  top: 50%;
  transform: translateY(-50%);
  right: 12px;
`;

// 새로운 모달 구조
const CustomModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 88vh;
  width: 90vw;
  max-width: 1200px;
  background: var(--backgroundColor);
  border-radius: var(--cardBorderRadius);
  overflow: hidden;
  position: relative;
`;

const FixedHeader = styled.div`
  flex-shrink: 0;
  height: 60px;
  background: var(--backgroundColor);
  border-bottom: 1px solid var(--lineColor);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const SingleImageContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const MultiImageContainer = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
  position: relative;
  overflow: hidden;
  padding: 20px;
`;

const MultiImageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  ${scrollbarStyle}
`;

const ImageItem = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  img {
    width: 100%;
    height: auto;
    border-radius: var(--cardBorderRadius);
    display: block;
  }
`;

const PreviewPanel = styled.div`
  width: 110px;
  flex-shrink: 0;
  border-left: 1px solid var(--lineColor);
  padding-left: 12px;
  overflow-y: auto;
  ${scrollbarStyle}
`;

const ThumbnailButton = styled.button<{ $active: boolean }>`
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 10px;
  cursor: pointer;
  overflow: hidden;
  background: transparent;
  box-shadow: ${({ $active }) =>
    $active ? '0 0 0 2px var(--pointColor)' : '0 0 0 1px var(--lineColor)'};
  opacity: ${({ $active }) => ($active ? 1 : 0.75)};
  transition: opacity 0.15s ease, box-shadow 0.15s ease;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 72px;
  object-fit: cover;
  display: block;
`;

type Props = {
  docId?: string | any;
  close?: () => void;
};
function GalleryDetailModal({ docId, close }: Props) {
  const collectionPath = getPathKey(1);

  const [_docID, setDocID] = useState(docId);

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const imageItemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const { data: galleryData } = useDocument<IgalleryItem>(
    `${collectionPath}/${_docID}`,
    { listen: true },
  );

  const [dir, setDir] = useState(1);

  const handlePrev = useCallback(async () => {
    setDir(0);

    const prevData = await prevPage({
      collectionPath,
      currentDocId: _docID!,
      orderBy: 'date',
    });
    if (prevData) setDocID(prevData.id);
  }, [_docID]);

  const handleNext = useCallback(async () => {
    setDir(1);
    const nextData = await nextPage({
      collectionPath,
      currentDocId: _docID!,
      orderBy: 'date',
    });
    if (nextData) setDocID(nextData.id);
  }, [_docID]);
  // prev, next document end -----------------------------------------

  const handleDelete = useCallback(async () => {
    if (confirm('갤러리를 삭제하시겠습니까?')) {
      await deleteGallery({
        docId: _docID!,
        imgURL: galleryData ? galleryData.imgURL || '' : '',
        hashtagIdList: galleryData?.hashtagIdList,
        collectionPath,
      });
      if (close) close();
    } else {
      return;
    }
  }, [_docID, collectionPath, galleryData]);

  const handleEdit = useCallback(() => {
    setOpenEditModal(true);
  }, []);

  useLayoutEffect(() => {
    //바깥 스크롤 막기
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useLayoutEffect(() => {
    setSelectedImageIndex(0);
    imageItemRefs.current = [];
  }, [_docID]);

  const handleThumbnailClick = useCallback((index: number) => {
    setSelectedImageIndex(index);
    imageItemRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const readPermission = useReadPermission({
    accessType: galleryData?.accessType || ACCESS_TYPE.PUBLIC,
    authorUid: galleryData?.authorUid || '',
  });
  const hasPreviewPanel = (galleryData?.imgURLList?.length || 0) > 1;

  return (
    <>
      <ModalWrapper
        outsideClick={close}
        contentStyle={{
          padding: 0,
          overflow: 'hidden',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CustomModalContainer>
          <FixedHeader>
            <CloseBtn onClick={close} color={'var(--textColor)'} />

            <div style={{ textAlign: 'left', flex: 1, marginLeft: '20px' }}>
              <div
                style={{
                  fontSize: 16,
                  color: 'var(--textColor)',
                  fontWeight: 700,
                }}
              >
                {galleryData && galleryData.title}
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--subTextColor)',
                  marginTop: 4,
                  lineHeight: 1,
                }}
              >
                {galleryData &&
                  galleryData.accessType !== ACCESS_TYPE.PUBLIC && (
                    <span>{ACCESS_TYPE_MAP[galleryData!.accessType]}</span>
                  )}
                {galleryData &&
                  galleryData.accessType !== ACCESS_TYPE.PUBLIC &&
                  galleryData.description && <span>&nbsp;•&nbsp;</span>}
                {galleryData && galleryData.description && (
                  <span>{galleryData && galleryData.description}</span>
                )}
              </p>
            </div>

            <ControlIcon
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              authorUid={galleryData?.authorUid || ''}
            />
          </FixedHeader>

          <ScrollableContent>
            <AnimatePresence custom={dir} exitBeforeEnter>
              {!readPermission ? (
                <GuardPage
                  accessType={galleryData?.accessType || ACCESS_TYPE.PUBLIC}
                  type="contents"
                />
              ) : (
                <>
                  {galleryData?.imgURL ? (
                    // 단일 이미지: 모달 높이에 맞춰 핏
                    <SingleImageContainer>
                      <FitImage
                        dir={dir}
                        customKey={_docID!}
                        imgTitle={galleryData?.title || ''}
                        imgURL={galleryData?.imgURL || ''}
                      />
                      <SlideLeftBtn className="slideLeftBtn" onClick={handlePrev}>
                        <ArrowBtn direction="left" />
                      </SlideLeftBtn>
                      <SlideRightBtn className="slideRightBtn" onClick={handleNext}>
                        <ArrowBtn direction="right" />
                      </SlideRightBtn>
                    </SingleImageContainer>
                  ) : (
                    // 다중 이미지: 원본 크기로 나열하여 스크롤
                    <MultiImageContainer>
                      <MultiImageList>
                        {galleryData?.imgURLList?.map((url, i) => (
                          <ImageItem
                            key={i}
                            ref={(element) => {
                              imageItemRefs.current[i] = element;
                            }}
                            onMouseEnter={() => setSelectedImageIndex(i)}
                          >
                            <img src={url} alt={`Image ${i + 1}`} />
                          </ImageItem>
                        ))}
                      </MultiImageList>
                      {hasPreviewPanel && (
                        <PreviewPanel>
                          {galleryData?.imgURLList?.map((url, i) => (
                            <ThumbnailButton
                              key={`thumb-${i}`}
                              type="button"
                              $active={selectedImageIndex === i}
                              onClick={() => handleThumbnailClick(i)}
                            >
                              <ThumbnailImage src={url} alt={`Thumbnail ${i + 1}`} />
                            </ThumbnailButton>
                          ))}
                        </PreviewPanel>
                      )}
                      <SlideLeftBtn className="slideLeftBtn" onClick={handlePrev}>
                        <ArrowBtn direction="left" />
                      </SlideLeftBtn>
                      <SlideRightBtn className="slideRightBtn" onClick={handleNext}>
                        <ArrowBtn direction="right" />
                      </SlideRightBtn>
                    </MultiImageContainer>
                  )}
                </>
              )}
            </AnimatePresence>
          </ScrollableContent>
        </CustomModalContainer>
      </ModalWrapper>
      {openEditModal && (
        <UpdateGalleryModal
          docId={_docID}
          close={() => setOpenEditModal(false)}
        />
      )}
    </>
  );
}

export default GalleryDetailModal;
